import { useContext, useEffect, useRef, useState } from 'react';

import { Alert, Button, GlobalContext, Input } from 'lynkco-up-core';
import { ReactInputEventHandler } from 'lynkco-up-core/dist/types/types';

import { deleteUserData } from '../../../services/GDPR';
import { Divider, VerificationScopeSelect } from '../../../components';
import { InputWrapper } from '../../shared';
import { Endpoint, VerificationScope } from '../../../types';
import { getUserSafeServiceError } from '../../../utils/serviceError';
import { ALL_VERIFICATION_SCOPE, toVerificationTypeHeader } from '../../../utils/verificationScope';

const deleteUserDataErrorMessage =
  'We could not complete the delete request with the details provided. Check the input and try again.';

function DeleteUserDataContent() {
  const { alert, loading } = useContext(GlobalContext);
  const [customerNumber, setCustomerNumber] = useState('');
  const [caseId, setCaseId] = useState('');
  const [verificationScope, setVerificationScope] = useState<VerificationScope>(ALL_VERIFICATION_SCOPE);
  const [formErrors, setFormErrors] = useState({
    customerNumber: false,
    caseId: false,
  });
  const maxLengthErrorMessage = 'This value cannot exceed 50 characters.';
  const isSubmitDisabledForConnectedServices = !customerNumber || Object.values(formErrors).some(error => error);
  const isSubmitDisabledForCarSharing = !customerNumber || !caseId || Object.values(formErrors).some(error => error);
  const [resultError, setResultError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChangeCustomerNumber: ReactInputEventHandler = e => {
    setCustomerNumber(e.currentTarget.value);
  };

  const handleChangeCaseId: ReactInputEventHandler = e => {
    setCaseId(e.currentTarget.value);
  };

  async function handleSubmit(endpoint: Endpoint) {
    const isSubmitDisabled =
      endpoint === Endpoint.CAR_SHARING ? isSubmitDisabledForCarSharing : isSubmitDisabledForConnectedServices;

    if (isSubmitDisabled) {
      return;
    }

    if (resultError) {
      setResultError(null);
    }

    loading.show();

    try {
      await deleteUserData(caseId, customerNumber, endpoint, toVerificationTypeHeader(verificationScope));
      alert.show('Data successfully deleted.', 'success');
    } catch (error) {
      setResultError(getUserSafeServiceError(error, deleteUserDataErrorMessage));
    }

    loading.hide();
  }

  function handleDismissResultError() {
    setResultError(null);
  }

  useEffect(() => {
    setFormErrors({
      customerNumber: customerNumber.length > 50,
      caseId: caseId.length > 50,
    });
  }, [customerNumber, caseId]);

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, []);

  return (
    <div>
      <h2 className="font-medium text-xl mb-7">Delete user data</h2>
      {resultError && (
        <Alert
          type="error"
          heading="There was an error while trying to delete user data"
          body={`${resultError}`}
          onDismiss={handleDismissResultError}
        />
      )}
      <Divider />
      <InputWrapper label="Customer number*">
        <Input
          ref={inputRef}
          type="text"
          extraClasses="basis-2/3 -mt-1"
          onChange={handleChangeCustomerNumber}
          errorText={maxLengthErrorMessage}
          hasError={formErrors.customerNumber}
        />
      </InputWrapper>
      <Divider />
      <InputWrapper label="Case ID">
        <Input
          extraClasses="basis-2/3 -mt-1"
          onChange={handleChangeCaseId}
          errorText={maxLengthErrorMessage}
          hasError={formErrors.caseId}
        />
      </InputWrapper>
      <Divider />
      <InputWrapper label="Verification data">
        <VerificationScopeSelect value={verificationScope} onChange={setVerificationScope} />
      </InputWrapper>
      <Divider />
      <div className="flex justify-end mt-7">
        <Button
          extraClasses="mr-3"
          onClick={() => handleSubmit(Endpoint.CONNECTED_SERVICE)}
          isDisabled={isSubmitDisabledForConnectedServices}>
          Delete from Connected Services
        </Button>
        <Button onClick={() => handleSubmit(Endpoint.CAR_SHARING)} isDisabled={isSubmitDisabledForCarSharing}>
          Delete from Car Sharing
        </Button>
      </div>
    </div>
  );
}

export default DeleteUserDataContent;
